import React, { useState } from 'react';
import { Video, ArrowRight, Loader } from 'lucide-react';

export const JoinMeeting: React.FC = () => {
  const [meetingId, setMeetingId] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [role, setRole] = useState<'host' | 'participant'>('participant');
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleJoin = async () => {
    if (!meetingId.trim()) {
      setError('Please enter a meeting ID');
      return;
    }

    if (!participantName.trim()) {
      setError('Please enter your name');
      return;
    }

    setIsJoining(true);
    setError(null);

    try {
      // First, generate a real token from the debug endpoint
      const tokenResponse = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/debug/generate-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          meetingId: meetingId,
          participantName: participantName,
          isHost: role === 'host'
        })
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        throw new Error(errorData.error || 'Failed to generate token');
      }

      const { token } = await tokenResponse.json();

      // Redirect to the appropriate consultation page with the room ID and token
      const baseUrl = role === 'host' 
        ? '/templates/legal/session/host' 
        : '/templates/legal/session/participant';
      
      const params = new URLSearchParams({
        roomId: meetingId,
        participantName: participantName,
        authToken: token,
        // Add a debug flag to bypass normal flow
        debug: 'true'
      });

      window.location.href = `${baseUrl}?${params.toString()}`;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join meeting');
      setIsJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <Video className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Join Meeting</h1>
          <p className="text-gray-600">Debug Tool - Enter meeting ID to join</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={(e) => { e.preventDefault(); handleJoin(); }}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meeting ID
              </label>
              <input
                type="text"
                value={meetingId}
                onChange={(e) => setMeetingId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                placeholder="e.g., bbb1234567890"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                RealtimeKit meeting IDs typically start with "bbb"
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name
              </label>
              <input
                type="text"
                value={participantName}
                onChange={(e) => setParticipantName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your name"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Join As
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="participant"
                    checked={role === 'participant'}
                    onChange={(e) => setRole(e.target.value as 'participant')}
                    className="mr-2"
                  />
                  <span>Participant (Client)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="host"
                    checked={role === 'host'}
                    onChange={(e) => setRole(e.target.value as 'host')}
                    className="mr-2"
                  />
                  <span>Host (Attorney)</span>
                </label>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isJoining}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold flex items-center justify-center"
            >
              {isJoining ? (
                <>
                  <Loader className="animate-spin mr-2" size={20} />
                  Joining...
                </>
              ) : (
                <>
                  Join Meeting
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Debug Instructions:</h3>
            <ol className="text-xs text-gray-600 space-y-1">
              <li>1. Create a consultation request on /templates/legal/demo</li>
              <li>2. Accept it from the attorney dashboard</li>
              <li>3. Copy the meeting ID that appears</li>
              <li>4. Enter it here to join the meeting</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};